import React, { useRef, useState, useEffect } from 'react';
import FormField from '../common/FormField';
import Button from '../common/Button';
import {
  DebitOrderMandateDTO,
  DonationDTO,
  MemberDTO,
} from '../../services/apiClient';
import SignatureBox from '../common/SignatureBox';
import { useBankApi } from '../../hooks/useBankApi';

interface Props {
  member: MemberDTO;
  donation: DonationDTO;
  debitOrderMandate: DebitOrderMandateDTO;
  onDebitOrderMandateChange: (updated: Partial<DebitOrderMandateDTO>) => void;
  onMemberChange: (updated: Partial<MemberDTO>) => void;
  onNext: () => void;
  onBack: () => void;
}

const CaptureAccountDetails: React.FC<Props> = ({
  member,
  donation,
  debitOrderMandate,
  onDebitOrderMandateChange,
  onMemberChange,
  onNext,
  onBack,
}) => {
  const [errors, setErrors] = useState<{
    //MANDATE FIELDS
    email?: string;
    phoneNumber?: string;
    bankName?: string;
    accountNumber?: string;
    accountholder?: string;
    idNumber?: string; // Add this line
  }>({});

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const sigRef = useRef<any>();

  const { search: searchBanks } = useBankApi();
  const [banks, setBanks] = useState<Array<{
    value: string;
    label: string;
    branchCode: string;
  }>>([]);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleCaptureSignature = () => {
    const signatureDataUrl = sigRef.current?.getSignatureDataUrl();
    if (signatureDataUrl) {
      onDebitOrderMandateChange({ signatureImage: signatureDataUrl });
    }
  };

  const handleNext = async () => {
    const newErrors: typeof errors = {};

    if (!debitOrderMandate.email || !validateEmail(debitOrderMandate.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!debitOrderMandate.phoneNumber?.trim()) newErrors.email = 'Email is required';
    if (!debitOrderMandate.phoneNumber?.trim()) newErrors.phoneNumber = 'Contact number is required';
    if (!debitOrderMandate.bankName?.trim()) newErrors.bankName = 'Bank name is required';
    if (!debitOrderMandate.accountHolder?.trim()) newErrors.accountholder = 'Accountholder is required';
    if (!debitOrderMandate.accountNumber?.trim()) newErrors.accountNumber = 'Account number is required';
    if (!debitOrderMandate.idNumber?.trim()) newErrors.idNumber = 'ID number is required';

    if (
      Object.keys(newErrors).length > 0
    ) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setIsSubmitting(true);

    handleCaptureSignature();

    try {
      onNext();
    } catch (error) {
      console.error('Failed to save member or answers:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMandateFields = () => {
    if (donation.frequency !== 'once') {
      return (
        <div>
          <FormField
            id="email"
            label="Email"
            type="email"
            value={member.email || ''}
            onChange={(e) => {
              onMemberChange({ email: e.target.value });
              if (errors.email)
                setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            placeholder="Enter your email"
            error={errors.email}
          />

          <FormField
            id="idNumber"
            label="ID Number"
            type="text"
            value={debitOrderMandate.idNumber || ''}
            onChange={(e) => {
              onDebitOrderMandateChange({ idNumber: e.target.value });
              if (errors.idNumber)
                setErrors((prev) => ({ ...prev, idNumber: undefined }));
            }}
            placeholder="Enter your ID number"
            error={errors.idNumber}
          />

          <FormField
            id="phoneNumber"
            label="Contact Number"
            type="text"
            value={debitOrderMandate.phoneNumber || ''}
            onChange={(e) => {
              onDebitOrderMandateChange({ phoneNumber: e.target.value });
              if (errors.phoneNumber)
                setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
            }}
            placeholder="Enter your contact number"
            error={errors.phoneNumber}
          />

          <FormField
            id="bankName"
            label="Bank Name"
            type="dropdown"
            value={debitOrderMandate.bankName || ''}
            onChange={(e) => {
              const selectedBank = banks.find(bank => bank.value === e.target.value);
              onDebitOrderMandateChange({ 
                bankName: e.target.value,
                branchCode: selectedBank?.branchCode || ''
              });
              if (errors.bankName)
                setErrors((prev) => ({ ...prev, bankName: undefined }));
            }}
            options={banks}
            placeholder="Select your bank"
            error={errors.bankName}
          />

          <FormField
            id="accountholder"
            label="Account Holder"
            type="text"
            value={debitOrderMandate.accountHolder || ''}
            onChange={(e) => {
              onDebitOrderMandateChange({ accountHolder: e.target.value });
              if (errors.accountholder)
                setErrors((prev) => ({ ...prev, accountHolder: undefined }));
            }}
            placeholder="Enter account holders name"
            error={errors.accountholder}
          />

          <FormField
            id="accountNumber"
            label="Account Number"
            type="text"
            value={debitOrderMandate.accountNumber || ''}
            onChange={(e) => {
              onDebitOrderMandateChange({ accountNumber: e.target.value });
              if (errors.accountNumber)
                setErrors((prev) => ({ ...prev, accountNumber: undefined }));
            }}
            placeholder="Enter your Account Number"
            error={errors.accountNumber}
          />

          <FormField
            id="branchCode"
            label="Branch Code"
            type="text"
            value={debitOrderMandate.branchCode || ''}
            onChange={(e) => {
              onDebitOrderMandateChange({ branchCode: e.target.value });
            }}
            placeholder="Branch code will be auto-populated"
            disabled={true}
          />

          <div className="text-left">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type of Account
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant={debitOrderMandate.accountType === 'cheque' ? 'primary' : 'secondary'}
                onClick={() => {
                  onDebitOrderMandateChange({ accountType: 'cheque' });
                }}
              >
                Cheque
              </Button>

              <Button
                type="button"
                variant={debitOrderMandate.accountType === 'savings' ? 'primary' : 'secondary'}
                onClick={() => {
                  onDebitOrderMandateChange({ accountType: 'savings' });
                }}
              >
                Savings
              </Button>

              <Button
                type="button"
                variant={debitOrderMandate.accountType === 'transmission' ? 'primary' : 'secondary'}
                onClick={() => {
                  onDebitOrderMandateChange({ accountType: 'transmission' });
                }}
              >
                Transmission
              </Button>
            </div>
          </div>

        </div>
      )
    }
  }

  const renderSignatureBox = () => {
    if (donation.frequency !== 'once') {
      return (
        <div>
          <h2 className="mb-4 text-lg font-semibold">Please Sign Below</h2>
          <SignatureBox ref={sigRef} />
        </div>
      )
    }
  }

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await searchBanks({
          page: 1,
          pageSize: 99999,
          sortBy: 'Name',
          sortDescending: false
        });

        if (response?.data?.results) {
          const bankOptions = response.data.results.map((bank: any) => ({
            value: bank.name,
            label: bank.name,
            branchCode: bank.branchCode
          }));
          setBanks(bankOptions);
        }
      } catch (error) {
        console.error('Error fetching banks:', error);
      }
    };

    fetchBanks();
  }, []);

  return (
    <div>
      {renderMandateFields()}
      {renderSignatureBox()}

      <div className="mt-4 flex justify-between">
        <Button onClick={onBack} variant="secondary">
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default CaptureAccountDetails;
